import {useCallback, useMemo, useState} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import ReactSelect, {components, OptionProps} from 'react-select';
import TableMetronic from '../../../app/common/components/table/TableMetronic';
import {EtatLien, ListService} from '../Types/service';
import {useIntl} from 'react-intl';
import {getStatusColor, getStatusLabel} from '../Helpers/stateHelper';
import {
  accessorService,
  accessorSite,
  getBuColLabel,
  getServiceColLabel,
  getSiteColLabel,
  getTelephonyDetailsPath,
  shouldOpenInternalTelephonyDetails,
} from '../Helpers/ServiceHelper';
import {createSearchParams, useNavigate} from 'react-router-dom';
import EtatBadge from './EtatBadge';
import ConnexionDot from './ConnexionDot';
import ServiceChildrenTable from './ServiceChildrenTable';
import styles from './Services.module.scss';
import {getEncryptedURIV1} from '../../../app/common/components/ECV1Link';
import {useGetMe} from '../../../app/hooks/useGetMe';
import {useClientLogin} from '../../../app/hooks/useClientLogin';
import {isClientCIS} from '../../Auth/Utils/CustomerHelper';
import {getG2000Link} from '../../../app/pages/dashboard/api';
//import ActionIcon from '../../../Components/ActionIcon';
//import {useGetClientType} from '../../../app/hooks/useClientType';
//import {isDivopClient} from '../../Auth/Utils/CustomerHelper';
import ApsRequestModal from '../../Divops/Components/ApsRequestModal';
// import ECV1Link from '../../../app/common/components/ECV1Link';

// Bouton « Demande APS » masqué temporairement
const SHOW_APS_REQUEST = false;

interface AccountOption {
  value: string;
  buId: number;
  buName: string;
  codeX3: string;
  count: number;
  label: string;
}

interface SiteOption {
  value: number;
  label: string;
  count: number;
}

const filterSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
};

interface ServicesProps {
  limit?: number;
  withSearchBar?: boolean;
  customLinkTitle?: string;
  customLinkRoute?: string;
  isLoading?: boolean;
  services?: ListService[];
  hideIfEmpty?: boolean;
  title?: string;
  enableRowDetail?: boolean;
  showConnexionInsights?: boolean;
  showTypeFilter?: boolean;
  showCategoryFilter?: boolean;
  detailRoute?: (service: ListService) => string;
  enableSubServices?: boolean;
  stackMobileStatusBadge?: boolean;
}

type ServiceCategory = 'all' | 'connexion' | 'telephony' | 'domain' | 'mail' | 'infrastructure';

const matchesCategory = (service: ListService, category: ServiceCategory): boolean => {
  switch (category) {
    case 'connexion':
      return service.type?.some((t) => t === 'Connexion' || t === 'VPN') ?? false;
    case 'telephony':
      return service.type?.includes('TELEPHONY') ?? false;
    case 'domain':
      return service.is_domain || (service.type?.includes('DOMAIN') ?? false);
    case 'mail':
      return service.type?.includes('MAIL') ?? false;
    case 'infrastructure':
      return service.type?.some((t) => t === 'BAY' || t === 'SERVER') ?? false;
    default:
      return true;
  }
};

const CATEGORY_ORDER: Exclude<ServiceCategory, 'all'>[] = [
  'connexion',
  'telephony',
  'domain',
  'mail',
  'infrastructure',
];

const CATEGORY_I18N_KEYS: Record<Exclude<ServiceCategory, 'all'>, string> = {
  connexion: 'SERVICES.CATEGORY.CONNEXIONS',
  telephony: 'SERVICES.CATEGORY.TELEPHONY',
  domain: 'SERVICES.CATEGORY.DOMAINS',
  mail: 'SERVICES.CATEGORY.MAILS',
  infrastructure: 'SERVICES.CATEGORY.INFRASTRUCTURE',
};

const getServiceCategory = (service: ListService): Exclude<ServiceCategory, 'all'> | null =>
  CATEGORY_ORDER.find((category) => matchesCategory(service, category)) ?? null;

const Services = ({
  customLinkTitle,
  customLinkRoute,
  limit = 0,
  withSearchBar = true,
  isLoading,
  services,
  hideIfEmpty = false,
  title,
  enableRowDetail = false,
  showConnexionInsights = true,
  showTypeFilter = true,
  showCategoryFilter = false,
  detailRoute,
  enableSubServices = false,
  stackMobileStatusBadge = false,
}: ServicesProps): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const customer = useGetMe();
  const userLogin = useClientLogin();
  const [selectedAccounts, setSelectedAccounts] = useState<AccountOption[]>([]);
  const [selectedSites, setSelectedSites] = useState<SiteOption[]>([]);
  const [connexionFilter, setConnexionFilter] = useState<
    'all' | 'CONNECTED' | 'DISCONNECTED' | 'TO_CONFIGURE'
  >('all');
  const [selectedType, setSelectedType] = useState<'all' | 'filaire' | '4g'>('all');
  const [apsRequestRow, setApsRequestRow] = useState<ListService | null>(null);

  //const {data: clientType} = useGetClientType();
  //const isDivop = isDivopClient(clientType);
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory>('all');

  const accountOptions = useMemo<AccountOption[]>(() => {
    if (!services) return [];
    const map = new Map<string, AccountOption>();
    services.forEach((s) => {
      if (s.buId == null) return;
      const buName = s.buName ?? '';
      const codeX3 = s.codeX3 ?? '';
      const key = `${s.buId}|${codeX3}|${buName}`;
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
        return;
      }
      const label = [s.buId, codeX3, buName].filter((p) => p !== '' && p != null).join(' - ');
      map.set(key, {value: key, buId: s.buId, buName, codeX3, label, count: 1});
    });
    return Array.from(map.values()).sort(
      (a, b) =>
        a.buId - b.buId || a.codeX3.localeCompare(b.codeX3) || a.buName.localeCompare(b.buName)
    );
  }, [services]);

  const siteOptions = useMemo<SiteOption[]>(() => {
    if (!services) return [];
    const map = new Map<number, SiteOption>();
    services.forEach((s) => {
      const site = s.site;
      if (site?.id == null) return;
      const existing = map.get(site.id);
      if (existing) {
        existing.count += 1;
        return;
      }
      const label = `${site.id} - ${[site.address, site.zipCode, site.city]
        .filter(Boolean)
        .join(' ')}`.trim();
      map.set(site.id, {value: site.id, label, count: 1});
    });
    return Array.from(map.values()).sort((a, b) => a.value - b.value);
  }, [services]);

  const baseFilteredServices = useMemo(() => {
    if (!services) return services;
    if (selectedAccounts.length === 0 && selectedSites.length === 0) return services;
    const accountKeys = new Set(selectedAccounts.map((o) => o.value));
    const siteIds = new Set(selectedSites.map((o) => o.value));
    return services.filter((s) => {
      const accountOk =
        accountKeys.size === 0 ||
        (s.buId != null && accountKeys.has(`${s.buId}|${s.codeX3 ?? ''}|${s.buName ?? ''}`));
      const siteOk = siteIds.size === 0 || (s.site?.id != null && siteIds.has(s.site.id));
      return accountOk && siteOk;
    });
  }, [services, selectedAccounts, selectedSites]);

  const connexionCounts = useMemo(() => {
    const list = baseFilteredServices ?? [];
    let connected = 0;
    let disconnected = 0;
    let toConfigure = 0;
    list.forEach((s) => {
      const etat = s.monitoring?.etat;
      if (etat === 'CONNECTED') connected += 1;
      else if (etat === 'DISCONNECTED') disconnected += 1;
      else toConfigure += 1;
    });
    return {
      all: list.length,
      CONNECTED: connected,
      DISCONNECTED: disconnected,
      TO_CONFIGURE: toConfigure,
    };
  }, [baseFilteredServices]);

  const filteredServices = useMemo(() => {
    if (!baseFilteredServices) return baseFilteredServices;
    if (connexionFilter === 'all' && selectedType === 'all' && selectedCategory === 'all') {
      return baseFilteredServices;
    }
    return baseFilteredServices.filter((s) => {
      // Filtre Etat (connexion)
      if (connexionFilter !== 'all') {
        const etat = s.monitoring?.etat;
        if (connexionFilter === 'TO_CONFIGURE') {
          if (etat === 'CONNECTED' || etat === 'DISCONNECTED') return false;
        } else if (etat !== connexionFilter) {
          return false;
        }
      }
      // Filtre Type
      if (selectedType !== 'all') {
        const is4g = !!s.is4g;
        if (selectedType === '4g' && !is4g) return false;
        if (selectedType === 'filaire' && is4g) return false;
      }
      // Filtre Catégorie
      if (selectedCategory !== 'all' && !matchesCategory(s, selectedCategory)) {
        return false;
      }
      return true;
    });
  }, [baseFilteredServices, connexionFilter, selectedType, selectedCategory]);

  const connexionStatCards: {
    key: 'all' | 'CONNECTED' | 'DISCONNECTED' | 'TO_CONFIGURE';
    label: string;
    count: number;
    color: string;
    icon: string;
  }[] = [
    {
      key: 'all',
      label: intl.formatMessage({id: 'SERVICES.CONNEXION.ALL'}),
      count: connexionCounts.all,
      color: '#7E8299',
      icon: 'bi-list-ul',
    },
    {
      key: 'CONNECTED',
      label: intl.formatMessage({id: 'SERVICES.CONNEXION.CONNECTED'}),
      count: connexionCounts.CONNECTED,
      color: '#50cd89',
      icon: 'bi-check-circle',
    },
    {
      key: 'DISCONNECTED',
      label: intl.formatMessage({id: 'SERVICES.CONNEXION.DISCONNECTED'}),
      count: connexionCounts.DISCONNECTED,
      color: '#f1416c',
      icon: 'bi-x-circle',
    },
    {
      key: 'TO_CONFIGURE',
      label: intl.formatMessage({id: 'SERVICES.CONNEXION.TO_CONFIGURE'}),
      count: connexionCounts.TO_CONFIGURE,
      color: '#F1A340',
      icon: 'bi-gear',
    },
  ];

  const CheckboxOption = <T extends {label: string; count?: number}>(
    props: OptionProps<T, true>
  ) => (
    <components.Option {...props}>
      <div className="d-flex align-items-start">
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null}
          className="me-2 mt-1 flex-shrink-0"
        />
        <label className="mb-0 flex-grow-1" style={{wordBreak: 'break-word'}}>
          {props.label}
        </label>
        {props.data.count != null && (
          <span className="ms-2 text-muted flex-shrink-0">({props.data.count})</span>
        )}
      </div>
    </components.Option>
  );

  // Navigation pour les catégories "détail" (routeur/4G/téléphonie/mail/domaine/baie/vpn/connexion) —
  // utilisée pour rendre la ligne cliquable plutôt que d'afficher une icône dédiée dans une
  // colonne Actions.
  const getRowDetailNavigation = useCallback(
    (row: ListService): (() => void) | null => {
      if (row.router?.type === 'Microtik' && row.router.data?.cis_id) {
        const idRouter = row.router.data.cis_id;
        return () =>
          window.open(getEncryptedURIV1(`routeurs/r7/c/${idRouter}`, userLogin), '_blank');
      }

      if (row.is4g) {
        return () => {
          const searchParams = new URLSearchParams();
          searchParams.append('cisId', row.serviceId != null ? String(row.serviceId) : '');
          navigate({
            pathname: `/services/consommationLTE`,
            search: createSearchParams(searchParams).toString(),
          });
        };
      }

      if (row.router?.type === 'N/A') {
        return () => {
          const searchParams = new URLSearchParams();
          searchParams.append('routerId', row.router?.data?.cis_id ?? '-1');
          searchParams.append('site', row.site?.id ? row.site.id.toString() : '');
          navigate({
            pathname: `/support`,
            search: createSearchParams(searchParams).toString(),
          });
        };
      }

      if (row.type?.includes('TELEPHONY')) {
        return () => {
          if (!isClientCIS(customer)) {
            window.open('https://extranet2.stella-telecom.fr/telephonie-fixe', '_blank');
            return;
          }
          const openDetails = () => navigate(getTelephonyDetailsPath(row));
          const site = row.site;
          if (shouldOpenInternalTelephonyDetails(row) || !site) {
            openDetails();
            return;
          }
          getG2000Link(site.id.toString(), userLogin)
            .then((link) => window.open(link, '_blank'))
            .catch(openDetails);
        };
      }

      if (row.type?.includes('MAIL')) {
        if (!isClientCIS(customer)) return null;
        return () => {
          const searchParams = new URLSearchParams();
          searchParams.append('buId', row.buId.toString());
          searchParams.append('link_name', row.link_name.toString());
          searchParams.append('serviceId', row.serviceId.toString());
          navigate({
            pathname: `/services/domain/emails`,
            search: createSearchParams(searchParams).toString(),
          });
        };
      }

      if (row.is_domain || row.type?.includes('DOMAIN')) {
        return () => {
          if (!isClientCIS(customer)) {
            window.open(
              `https://extranet2.stella-telecom.fr/domaine/details/domaine/${row.link_name}`,
              '_blank'
            );
            return;
          }
          const searchParams = new URLSearchParams();
          searchParams.append('domaine', row.link_name.toString());
          navigate({
            pathname: `/services/domaine/infos`,
            search: createSearchParams(searchParams).toString(),
          });
        };
      }

      if (row.type?.includes('BAY')) {
        return () => {
          const searchParams = new URLSearchParams();
          searchParams.append('serviceId', row.serviceId != null ? String(row.serviceId) : '');
          navigate({
            pathname: `/services/baies/details`,
            search: createSearchParams(searchParams).toString(),
          });
        };
      }

      if (row.type?.includes('VPN')) {
        return () => navigate(`/services/connexion/vpn/${row.serviceId}`);
      }

      if (row.type?.includes('Connexion')) {
        return () => navigate(`/services/connexion/liens/${row.serviceId}`);
      }

      return null;
    },
    [customer, navigate, userLogin]
  );

  const columns = useMemo<ColumnDef<ListService>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'SERVICES.COL.SERVICE'}),
        accessorKey: 'service',
        cell: getServiceColLabel,
        accessorFn: accessorService,

        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.serviceId ?? 0;
          const idB = rowB.original.serviceId ?? 0;
          return idA - idB;
        },
      },
      {
        header: intl.formatMessage({id: 'SERVICES.COL.ACCOUNT'}),
        accessorKey: 'buId',
        cell: getBuColLabel,
        size: 150,
      },
      {
        header: intl.formatMessage({id: 'SERVICES.COL.SITE'}),
        accessorKey: 'siteId',
        cell: getSiteColLabel,
        accessorFn: accessorSite,
        size: 200,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.site?.id ?? 0;
          const idB = rowB.original.site?.id ?? 0;
          return idA - idB;
        },
      },
      ...(showCategoryFilter
        ? [
            {
              header: intl.formatMessage({id: 'SERVICES.COL.CATEGORY'}),
              accessorKey: 'category',
              size: 150,
              accessorFn: (row: ListService) => {
                const category = getServiceCategory(row);
                return category ? intl.formatMessage({id: CATEGORY_I18N_KEYS[category]}) : '-';
              },
              cell: (info) => info.getValue<string>(),
            } as ColumnDef<ListService>,
          ]
        : []),
      ...(showConnexionInsights
        ? [
            {
              header: intl.formatMessage({id: 'SERVICES.COL.TYPE'}),
              accessorKey: 'type',
              size: 50,
              accessorFn: (row) =>
                row.is4g ? '4G' : intl.formatMessage({id: 'SERVICES.TYPE.WIRED'}),
              cell: (info) =>
                info.row.original.is4g ? '4G' : intl.formatMessage({id: 'SERVICES.TYPE.WIRED'}),
            } as ColumnDef<ListService>,
          ]
        : []),
      {
        header: intl.formatMessage({id: 'SERVICES.COL.STATUS'}),
        accessorKey: 'state',
        size: 90,
        cell: (info) => {
          const status = info.row.original.state;
          const color = getStatusColor(status);
          return status ? <EtatBadge color={color} label={getStatusLabel(status, intl)} /> : '-';
        },
      },
      ...(showConnexionInsights
        ? [
            {
              header: intl.formatMessage({id: 'SERVICES.COL.STATE'}),
              accessorKey: 'connexion',
              size: 80,
              accessorFn: (row) => row.monitoring?.etat ?? '',
              cell: (info) => {
                const monitoring = info.row.original.monitoring;
                const etat = monitoring?.etat as EtatLien | undefined;
                if (!etat) return '-';
                if (etat === 'CONNECTED')
                  return (
                    <ConnexionDot
                      color="#50cd89"
                      label={intl.formatMessage({id: 'LIEN.DETAIL.STATE.CONNECTED'})}
                    />
                  );
                if (etat === 'DISCONNECTED')
                  return (
                    <ConnexionDot
                      color="#f1416c"
                      label={intl.formatMessage({id: 'LIEN.DETAIL.STATE.DISCONNECTED'})}
                    />
                  );
                return (
                  <ConnexionDot
                    color="#a1a5b7"
                    label={intl.formatMessage({id: 'SERVICES.CONNEXION.TO_CONFIGURE'})}
                  />
                );
              },
            } as ColumnDef<ListService>,
          ]
        : []),
      ...(enableRowDetail || showCategoryFilter
        ? [
            {
              header: '',
              accessorKey: 'detailArrow',
              size: 40,
              enableSorting: false,
              cell: () => (
                <div className="d-flex align-items-center justify-content-end pe-3">
                  <i className="bi bi-chevron-right text-muted" />
                </div>
              ),
            } as ColumnDef<ListService>,
          ]
        : []),
    ],
    [enableRowDetail, showConnexionInsights, showCategoryFilter, intl]
  );

  const renderMobileCard = (row: ListService): JSX.Element => {
    const status = row.state;
    const etat = row.monitoring?.etat as EtatLien | undefined;
    const cellInfo = {row: {original: row}};

    return (
      <div className={styles.mobileCardBody}>
        <div
          className={styles.mobileCardHeader}
          style={
            stackMobileStatusBadge
              ? {flexDirection: 'column', alignItems: 'flex-start', gap: '0.35rem'}
              : undefined
          }
        >
          {stackMobileStatusBadge && status && (
            <EtatBadge color={getStatusColor(status)} label={getStatusLabel(status, intl)} />
          )}
          {getServiceColLabel(cellInfo)}
          {!stackMobileStatusBadge && status && (
            <EtatBadge color={getStatusColor(status)} label={getStatusLabel(status, intl)} />
          )}
        </div>
        <div>
          <span className={styles.mobileCardSectionLabel}>
            {intl.formatMessage({id: 'SERVICES.COL.ACCOUNT'})}
          </span>
          {getBuColLabel(cellInfo)}
        </div>
        <div>
          <span className={styles.mobileCardSectionLabel}>
            {intl.formatMessage({id: 'SERVICES.COL.SITE'})}
          </span>
          {getSiteColLabel(cellInfo)}
        </div>
        {showCategoryFilter && (
          <div className={styles.mobileCardRow}>
            <span className={styles.mobileCardLabel}>
              {intl.formatMessage({id: 'SERVICES.COL.CATEGORY'})}
            </span>
            <span>
              {(() => {
                const category = getServiceCategory(row);
                return category ? intl.formatMessage({id: CATEGORY_I18N_KEYS[category]}) : '-';
              })()}
            </span>
          </div>
        )}
        {showConnexionInsights && (
          <div className={styles.mobileCardRow}>
            <span className={styles.mobileCardLabel}>
              {intl.formatMessage({id: 'SERVICES.COL.TYPE'})}
            </span>
            <span>{row.is4g ? '4G' : intl.formatMessage({id: 'SERVICES.TYPE.WIRED'})}</span>
          </div>
        )}
        {showConnexionInsights && (
          <div className={styles.mobileCardRow}>
            <span className={styles.mobileCardLabel}>
              {intl.formatMessage({id: 'SERVICES.COL.STATE'})}
            </span>
            {etat === 'CONNECTED' ? (
              <ConnexionDot
                color="#50cd89"
                label={intl.formatMessage({id: 'LIEN.DETAIL.STATE.CONNECTED'})}
              />
            ) : etat === 'DISCONNECTED' ? (
              <ConnexionDot
                color="#f1416c"
                label={intl.formatMessage({id: 'LIEN.DETAIL.STATE.DISCONNECTED'})}
              />
            ) : (
              <ConnexionDot
                color="#a1a5b7"
                label={intl.formatMessage({id: 'SERVICES.CONNEXION.TO_CONFIGURE'})}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  if (hideIfEmpty && (!services || services.length === 0)) {
    return <div></div>;
  }

  const dataToDisplay = filteredServices ?? services;

  const typeOptions: {value: 'all' | 'filaire' | '4g'; label: string}[] = [
    {value: 'all', label: intl.formatMessage({id: 'SERVICES.CONNEXION.ALL'})},
    {value: 'filaire', label: intl.formatMessage({id: 'SERVICES.TYPE.WIRED'})},
    {value: '4g', label: '4G'},
  ];

  const categoryOptions: {value: ServiceCategory; label: string}[] = [
    {value: 'all', label: intl.formatMessage({id: 'SERVICES.CONNEXION.ALL'})},
    {value: 'connexion', label: intl.formatMessage({id: 'SERVICES.CATEGORY.CONNEXIONS'})},
    {value: 'telephony', label: intl.formatMessage({id: 'SERVICES.CATEGORY.TELEPHONY'})},
    {value: 'domain', label: intl.formatMessage({id: 'SERVICES.CATEGORY.DOMAINS'})},
    {value: 'mail', label: intl.formatMessage({id: 'SERVICES.CATEGORY.MAILS'})},
    {value: 'infrastructure', label: intl.formatMessage({id: 'SERVICES.CATEGORY.INFRASTRUCTURE'})},
  ];

  const filtersToolbar = (
    <>
      <div className={styles.filterSelectWrapper}>
        <ReactSelect<AccountOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={accountOptions}
          value={selectedAccounts}
          onChange={(vals) => setSelectedAccounts(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'SERVICES.FILTER.ALL_ACCOUNTS'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
      <div className={styles.filterSelectWrapperWide}>
        <ReactSelect<SiteOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={siteOptions}
          value={selectedSites}
          onChange={(vals) => setSelectedSites(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'SERVICES.FILTER.ALL_SITES'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
      {showConnexionInsights && showTypeFilter && (
        <div className="btn-group border border-primary rounded" role="group">
          {typeOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`btn btn-sm ${
                selectedType === opt.value
                  ? 'btn-primary active fw-bold'
                  : 'btn-light-primary text-primary text-hover-white'
              }`}
              onClick={() => setSelectedType(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
      {showCategoryFilter && (
        <div className="btn-group border border-primary rounded" role="group">
          {categoryOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`btn btn-sm ${
                selectedCategory === opt.value
                  ? 'btn-primary active fw-bold'
                  : 'btn-light-primary text-primary text-hover-white'
              }`}
              onClick={() => setSelectedCategory(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </>
  );

  return (
    <div className={styles.servicesTable}>
      {showConnexionInsights && (
        <div className="d-flex gap-3 mb-4 flex-wrap">
          {connexionStatCards.map((card) => {
            return (
              <div
                key={card.key}
                role="button"
                onClick={() => setConnexionFilter(card.key)}
                className="d-flex align-items-center bg-white rounded shadow-sm px-4 py-3 flex-grow-1"
                style={{minWidth: 0, cursor: 'pointer', border: '1px solid #e6e6e6'}}
              >
                <span
                  className="rounded-circle me-3"
                  style={{width: 8, height: 8, backgroundColor: card.color, flexShrink: 0}}
                />
                <div className="d-flex flex-column">
                  <span className="fw-bold fs-3 lh-1">{card.count}</span>
                  <span className="text-muted small mt-1">{card.label}</span>
                </div>
                <i
                  className={`bi ${card.icon} ms-auto ps-3 fs-2`}
                  style={{color: card.color, flexShrink: 0}}
                />
              </div>
            );
          })}
        </div>
      )}
      <TableMetronic<ListService>
        toolbar={filtersToolbar}
        stackedHeader
        rowClickable={
          enableRowDetail
            ? () => true
            : showCategoryFilter
            ? (row) => getRowDetailNavigation(row) != null
            : (row) => !!row.is4g
        }
        onRowClick={(row) => {
          if (enableRowDetail) {
            navigate(detailRoute ? detailRoute(row) : `/services/connexion/liens/${row.serviceId}`);
            return;
          }
          if (showCategoryFilter) {
            getRowDetailNavigation(row)?.();
            return;
          }
          const searchParams = new URLSearchParams();
          searchParams.append('cisId', row.serviceId != null ? String(row.serviceId) : '');
          navigate({
            pathname: `/services/consommationLTE`,
            search: createSearchParams(searchParams).toString(),
          });
        }}
        title={title ?? intl.formatMessage({id: 'MENU.CONNECTIONS'})}
        data={
          (limit && limit > 0
            ? dataToDisplay?.slice(dataToDisplay.length - limit, dataToDisplay.length)
            : dataToDisplay) ?? []
        }
        columns={columns}
        renderMobileCard={renderMobileCard}
        isLoading={isLoading}
        defaultSorting={[
          {
            id: 'siteId',
            desc: true,
          },
        ]}
        searchBar={withSearchBar}
        pagination={withSearchBar}
        customLinkTitle={customLinkTitle}
        customLinkRoute={customLinkRoute}
        getRowId={enableSubServices ? (row) => String(row.serviceId) : undefined}
        renderRowDetail={
          enableSubServices
            ? (row) =>
                row.serviceChildren && row.serviceChildren.length > 0 ? (
                  <ServiceChildrenTable services={row.serviceChildren} />
                ) : null
            : undefined
        }
      />
      {SHOW_APS_REQUEST && apsRequestRow && (
        <ApsRequestModal
          show={!!apsRequestRow}
          hide={() => setApsRequestRow(null)}
          buId={apsRequestRow.buId}
          buName={apsRequestRow.buName}
        />
      )}
    </div>
  );
};

export default Services;
